import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { detectGeo } from "./geo";

// Static reference rates from 1 MAD (base) — display only.
const RATES_FROM_MAD: Record<string, number> = {
  MAD: 1,
  EUR: 0.092,
  USD: 0.10,
  GBP: 0.079,
  CHF: 0.088,
  CAD: 0.137,
  AUD: 0.152,
  AED: 0.367,
  SAR: 0.375,
};

const LS_KEY = "bwt:currency";

export function convertFromMad(amountMad: number, currency: string): number {
  const rate = RATES_FROM_MAD[currency] ?? RATES_FROM_MAD.EUR;
  return amountMad * rate;
}

export function formatPrice(amountMad: number, currency: string, locale = "en"): string {
  const value = convertFromMad(amountMad, currency);
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return `${Math.round(value)} ${currency}`;
  }
}

const CURRENCY_EVENT = "bwt:currency-change";

export function useCurrency() {
  const [currency, setCurrencyState] = useState<string>(() => {
    if (typeof window === "undefined") return "EUR";
    return localStorage.getItem(LS_KEY) || "EUR";
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<string>).detail;
      if (detail) setCurrencyState(detail);
      else setCurrencyState(localStorage.getItem(LS_KEY) || "EUR");
    };
    window.addEventListener(CURRENCY_EVENT, handler);
    window.addEventListener("storage", handler);
    if (!localStorage.getItem(LS_KEY)) {
      detectGeo().then((g) => {
        if (!localStorage.getItem(LS_KEY)) {
          setCurrencyState(g.currency);
          try { localStorage.setItem(LS_KEY, g.currency); } catch { /* ignore */ }
        }
      });
    }
    return () => {
      window.removeEventListener(CURRENCY_EVENT, handler);
      window.removeEventListener("storage", handler);
    };
  }, []);

  const setCurrency = useCallback((c: string) => {
    setCurrencyState(c);
    try { localStorage.setItem(LS_KEY, c); } catch { /* ignore */ }
    try { window.dispatchEvent(new CustomEvent(CURRENCY_EVENT, { detail: c })); } catch { /* ignore */ }
  }, []);

  return { currency, setCurrency };
}

/**
 * Hook returning a formatter that converts a MAD amount to the user's
 * currently selected currency. Use everywhere prices are displayed so the
 * value reacts to the CurrencySwitcher.
 */
export function useFormatPrice() {
  const { currency } = useCurrency();
  const { i18n } = useTranslation();
  const locale = (i18n.language || "en").slice(0, 2);
  return useCallback(
    (amountMad: number) => formatPrice(amountMad, currency, locale),
    [currency, locale],
  );
}

